import type { ReactElement, Ref } from 'react';
import RelatedNews from 'components/blocks/related-news/RelatedNews';
import CampaignPage from 'components/blocks/campaign/CampaignPage';
import {
  CampaignStatisticsBlock,
  CampaignComparisonBlock,
  CampaignDownloadBlock,
} from 'components/blocks/campaign/CampaignBlocks';

/* PLOP_INJECT_TEMPLATE_IMPORT */
import AutoAccordion from 'components/blocks/auto-accordion/AutoAccordion';
import ImageGrid5 from 'components/blocks/image-grid5/ImageGrid5';
import StickyNotes from 'components/blocks/sticky-notes/StickyNotes';
import StoryCards from 'components/blocks/story-cards/StoryCards';
import HeaderRotatingShape from 'components/blocks/header-rotating-shape/HeaderRotatingShape';
import TextDropdown from 'components/blocks/text-dropdown/TextDropdown';
import ContactPage from 'components/blocks/contact-page/ContactPage';
import ListLinkItems from 'components/blocks/list-link-items/ListLinkItems';
import HeaderHighlight from 'components/blocks/header-highlight/HeaderHighlight';
import ReadMore from 'components/blocks/read-more/ReadMore';
import PersonDetails from 'components/blocks/person-details/PersonDetails';
import PersonOverview from 'components/blocks/person-overview/PersonOverview';
import CookieSettings from 'components/blocks/cookie-settings/CookieSettings';
import TitleTextColumns from 'components/blocks/title-text-columns/TitleTextColumns';
import AssetList from 'components/blocks/asset-list/AssetList';
import PardotPdfForm from 'components/blocks/pardot-pdf-form/PardotPdfForm';
import DescriptionList from 'components/blocks/description-list/DescriptionList';
import GridList from 'components/blocks/grid-list/GridList';
import Accordion from 'components/blocks/accordion/Accordion';
import ModernCreativityInfo from 'components/blocks/modern-creativity-info/ModernCreativityInfo';
import ButtonGridItem from 'components/blocks/button-grid/button-grid-item/ButtonGridItem';
import HeaderShowrealLarge from 'components/blocks/header-showreal-large/HeaderShowrealLarge';
import HorizontalCreativity from 'components/blocks/horizontal-creativity/HorizontalCreativity';
import HorizontalCreativitySingleImage from 'components/blocks/horizontal-creativity-single-image/HorizontalCreativitySingleImage';
import ServicePage from 'components/blocks/service-page/ServicePage';
import ImageSliderItem from 'components/blocks/image-slider-draggable/image-slider-item/ImageSliderItem';
import PeopleSliderItem from 'components/blocks/people-slider/people-slider-item/PeopleSliderItem';
import IntroColumnText from 'components/blocks/intro-column-text/IntroColumnText';
import Solution from 'components/blocks/solution/Solution';
import NewsOverviewItem from 'components/blocks/news-overview/news-overview-item/NewsOverviewItem';
import FeaturedNewsItem from 'components/blocks/featured-news-overview/featured-news-item/FeaturedNewsItem';
import NewsOverview from 'components/blocks/news-overview/NewsOverview';
import FeaturedNewsOverview from 'components/blocks/featured-news-overview/FeaturedNewsOverview';
import HeaderTitleH1 from 'components/blocks/header-title-h1/HeaderTitleH1';
import ButtonGrid from 'components/blocks/button-grid/ButtonGrid';
import TextGrid from 'components/blocks/text-grid/TextGrid';
import ImageSliderDraggable from 'components/blocks/image-slider-draggable/ImageSliderDraggable';
import PeopleSlider from 'components/blocks/people-slider/PeopleSlider';
import OfficeDetails from 'components/blocks/office-details/OfficeDetails';
import ImageText from 'components/blocks/image-text/ImageText';
import TitleAndText from 'components/blocks/title-and-text/TitleAndText';
import LocationOverview from 'components/blocks/location-overview/LocationOverview';
import CookieList from 'components/blocks/cookie-list/CookieList';
import Emails from 'components/blocks/emails/Emails';
import HeaderFullWidthTitle from 'components/blocks/header-full-width-title/HeaderFullWidthTitle';
import HeaderStandard from 'components/blocks/header-standard/HeaderStandard';
import ImageGridFluid from 'components/blocks/image-grid-fluid/ImageGridFluid';
import IntegratedSolutions from 'components/blocks/integrated-solutions/IntegratedSolutions';
import SolutionCard from 'components/blocks/integrated-solutions/solution-card/SolutionCard';
import Karaoke from 'components/blocks/karaoke/Karaoke';
import Location from 'components/blocks/location/Location';
import Locations from 'components/blocks/locations/Locations';
import ServicePowers from 'components/blocks/service-powers/ServicePowers';
import Services from 'components/blocks/services/Services';
import SliderContent from 'components/blocks/slider-content/SliderContent';
import Statistics from 'components/blocks/statistics/Statistics';
import TableBlock from 'components/blocks/table-block/TableBlock';
import Case from 'components/blocks/case/Case';
import ImageBlock from 'components/blocks/image-block/ImageBlock';
import Quote from 'components/blocks/quote/Quote';
import RichText from 'components/blocks/rich-text/RichText';
import VideoBlock from 'components/blocks/video-block/VideoBlock';
import Article from 'components/blocks/article/Article';
import BlackholeCta from 'components/blocks/blackhole-cta/BlackholeCta';
import BornAndRaised from 'components/blocks/born-and-raised/BornAndRaised';
import HeaderIntro from 'components/blocks/header-intro/HeaderIntro';
import ModernCreativity from 'components/blocks/modern-creativity/ModernCreativity';
import ServicesCtaItem from 'components/blocks/services-cta/services-cta-item/ServicesCtaItem';
import ServicesCta from 'components/blocks/services-cta/ServicesCta';
import TickerText from 'components/blocks/ticker-text/TickerText';
import CaseListing from 'components/blocks/case-listing/CaseListing';
import HeaderShowreel from 'components/blocks/header-showreel/HeaderShowreel';
import CaseCarousel from 'components/blocks/case-carousel/CaseCarousel';
import LatestNews from 'components/blocks/latest-news/LatestNews';
import Page from 'components/blocks/page/Page';
import Placeholder from 'components/utils/Placeholder';
import EmailForm from './blocks/email-form/EmailForm';
import Superpowers from './blocks/superpowers/Superpowers';
import type { StoryBlokComponentProps } from 'util/storyblok/types/component.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Components: { [key: string]: ReactElement | any } = {
  related_news: RelatedNews,
  campaign_page: CampaignPage,
  campaign_statistics: CampaignStatisticsBlock,
  campaign_comparison: CampaignComparisonBlock,
  campaign_download: CampaignDownloadBlock,
  /* PLOP_INJECT_TEMPLATE */
  auto_accordion: AutoAccordion,
  image_grid_5: ImageGrid5,
  sticky_notes: StickyNotes,
  story_cards: StoryCards,
  header_rotating_shape: HeaderRotatingShape,
  text_dropdown: TextDropdown,
  contact_page: ContactPage,
  list_link_items: ListLinkItems,
  header_highlight: HeaderHighlight,
  read_more: ReadMore,
  person_details: PersonDetails,
  person_overview: PersonOverview,
  cookie_settings: CookieSettings,
  title_text_columns: TitleTextColumns,
  asset_list: AssetList,
  pardot_pdf_form: PardotPdfForm,
  email_form: EmailForm,
  description_list: DescriptionList,
  grid_list: GridList,
  accordion: Accordion,
  modern_creativity_info: ModernCreativityInfo,
  button_grid_item: ButtonGridItem,
  header_showreal_large: HeaderShowrealLarge,
  horizontal_creativity: HorizontalCreativity,
  horizontal_creativity_single_image: HorizontalCreativitySingleImage,
  service_page: ServicePage,
  image_slider_item: ImageSliderItem,
  people_slider_item: PeopleSliderItem,
  intro_column_text: IntroColumnText,
  solution: Solution,
  news_overview_item: NewsOverviewItem,
  featured_news_item: FeaturedNewsItem,
  news_overview: NewsOverview,
  featured_news_overview: FeaturedNewsOverview,
  header_title_h1: HeaderTitleH1,
  button_grid: ButtonGrid,
  text_grid: TextGrid,
  image_slider_draggable: ImageSliderDraggable,
  people_slider: PeopleSlider,
  office_details: OfficeDetails,
  image_text: ImageText,
  title_and_text: TitleAndText,
  location_overview: LocationOverview,
  table_block: TableBlock,
  cookie_list: CookieList,
  service_powers: ServicePowers,
  superpowers: Superpowers,
  blackhole_cta: BlackholeCta,
  header_full_width_title: HeaderFullWidthTitle,
  solution_card: SolutionCard,
  integrated_solutions: IntegratedSolutions,
  services: Services,
  header_standard: HeaderStandard,
  karaoke: Karaoke,
  latest_news: LatestNews,
  emails_blok: Emails,
  locations_blok: Locations,
  locations: Location,
  statistics: Statistics,
  slider_content: SliderContent,
  image_grid_fluid: ImageGridFluid,
  case: Case,
  image_block: ImageBlock,
  video_block: VideoBlock,
  quote_block: Quote,
  rich_text: RichText,
  article: Article,
  modern_creativity: ModernCreativity,
  services_cta_item: ServicesCtaItem,
  born_and_raised: BornAndRaised,
  services_cta: ServicesCta,
  header_intro: HeaderIntro,
  ticker_text: TickerText,
  case_listing: CaseListing,
  header_showreel: HeaderShowreel,
  case_carousel: CaseCarousel,
  page: Page,
};

export type DynamicComponentProps = { canTransitionIn?: boolean } & StoryBlokComponentProps;
export type DynamicComponentRef = HTMLDivElement;

export default function DynamicComponent({
  className,
  blok,
  canTransitionIn,
  ref,
}: DynamicComponentProps & { ref?: Ref<DynamicComponentRef> }): ReactElement | null {
  if (blok && typeof Components[blok.component] !== 'undefined') {
    const Component = Components[blok.component];
    // console.log(blok.component);
    // const debugComponents = ['page', 'latest_news'];
    // const debugExcludeComponents = ['header_intro'];

    // if (!debugExcludeComponents.includes(blok.component)) {
    return (
      <Component
        blok={blok}
        key={blok._uid}
        className={className}
        ref={ref}
        canTransitionIn={canTransitionIn}
      />
    );
    // }
  }

  return blok && blok.component ? (
    <Placeholder componentName={blok.component} key={blok.component} />
  ) : null;
}
